import {
    CSSProperties,
    FC,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { Placement } from '@floating-ui/core';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';

import { styled } from '../theme';
import Text from './Text';

const TooltipContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: 'max-content',
    maxWidth: 350,
    zIndex: 1060,
});

const Container = styled.div(({ theme }) => ({
    background: 'rgba(255, 255, 255, 0.90)',
    boxShadow:
        '0 10px 10px 0 rgba(0, 0, 0, 0.04), 0 0 10px 0 rgba(0, 0, 0, 0.08), 0 20px 20px 0 rgba(0, 0, 0, 0.06)',
    backdropFilter: 'blur(5px)',
    padding: 12,
    borderRadius: theme.borderRadius.base,
}));

const ScrollableContent = styled('div', {
    shouldForwardProp: (prop) => prop !== 'maxHeight',
})<{ maxHeight: number }>(({ maxHeight }) => ({
    maxHeight,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarGutter: 'stable',
    marginRight: -12,
    paddingRight: 12,
    scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent',
    '&::-webkit-scrollbar': {
        width: 6,
        backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: 3,
    },
}));

const StyledText = styled(Text)(({ theme }) => ({
    color: theme.color.jetBlack,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.roman,
    lineHeight: '20px',
    display: 'block',
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
}));

type Props = PropsWithChildren & {
    className?: string;
    title?: ReactNode;
    style?: CSSProperties;
    textStyle?: CSSProperties;
    containerStyle?: CSSProperties;
    placement?: Placement;
    trigger?: 'click' | 'right-click' | 'hover' | 'focus' | 'none';
    wrapperStyles?: CSSProperties | null;
    offset?: number;
    /**
     * When set, the tooltip content is capped at this pixel height and becomes
     * vertically scrollable.
     */
    maxContentHeight?: number;
    /**
     * Delay (ms) before a hover/focus tooltip closes after the pointer leaves.
     * Bump this when the tooltip content hosts its own nested hover tooltip
     * (a portaled floating element isn't a DOM descendant of this one, so the
     * pointer briefly registers as "left" while crossing into it).
     */
    hideDelay?: number;
};

const Tooltip: FC<Props> = ({
    children,
    className,
    title,
    textStyle,
    containerStyle,
    trigger = 'hover',
    placement = 'top',
    wrapperStyles = {},
    style,
    maxContentHeight,
    hideDelay = 100,
    offset: offsetDistance = 12,
}) => {
    const [open, setOpen] = useState(false);
    const hideTimeoutRef = useRef(null);
    const hasTitle = title != null && title !== '';

    const {
        x,
        y,
        refs,
        strategy,
        placement: finalPlacement,
    } = useFloating({
        placement,
        middleware: [offset(offsetDistance), flip(), shift()],
        whileElementsMounted: autoUpdate,
    });

    const handleClickOutside = useCallback(
        (e: MouseEvent) => {
            if (!open || (trigger !== 'click' && trigger !== 'right-click')) return;
            const refEl = refs.reference.current;
            const floatEl = refs.floating.current;
            if (
                refEl &&
                'contains' in refEl &&
                !refEl.contains(e.target as Node) &&
                floatEl &&
                !floatEl.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        },
        [open, trigger, refs]
    );

    const clearHideTimeout = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            clearHideTimeout();
        };
    }, [handleClickOutside]);

    // Prevent rendering / persisting an "empty" tooltip.
    useEffect(() => {
        if (!hasTitle && open) {
            setOpen(false);
        }
    }, [hasTitle, open]);

    const showTooltip = useCallback(() => {
        if (trigger === 'hover' || trigger === 'focus') {
            clearHideTimeout();
            setOpen(true);
        }
    }, [trigger]);

    const hideTooltip = useCallback(() => {
        if (trigger === 'hover' || trigger === 'focus') {
            clearHideTimeout();
            hideTimeoutRef.current = setTimeout(() => {
                setOpen(false);
            }, hideDelay);
        }
    }, [trigger, hideDelay]);

    const toggleTooltip = useCallback(() => {
        if (trigger === 'click') setOpen((o) => !o);
    }, [trigger]);

    const onContextMenu = useCallback(
        (e: MouseEvent) => {
            if (trigger === 'right-click') {
                e.preventDefault();
                setOpen((o) => !o);
            }
        },
        [trigger]
    );

    return (
        <>
            <span
                ref={refs.setReference}
                style={wrapperStyles || {}}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip}
                onBlur={hideTooltip}
                onClick={trigger === 'click' ? toggleTooltip : undefined}
                onContextMenu={onContextMenu}
            >
                {children}
            </span>

            {open &&
                hasTitle &&
                createPortal(
                    <TooltipContainer
                        ref={refs.setFloating}
                        className={className}
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                            ...style,
                        }}
                        data-placement={finalPlacement}
                        onMouseEnter={showTooltip}
                        onMouseLeave={hideTooltip}
                    >
                        <Container style={containerStyle}>
                            {maxContentHeight ? (
                                <ScrollableContent maxHeight={maxContentHeight}>
                                    <StyledText style={textStyle}>{title}</StyledText>
                                </ScrollableContent>
                            ) : (
                                <StyledText style={textStyle}>{title}</StyledText>
                            )}
                        </Container>
                    </TooltipContainer>,
                    document.body
                )}
        </>
    );
};

export default Tooltip;
